import { DestroyRef, inject, Injector } from '@angular/core';
import { Api } from 'bimplus-websdk';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { parseToken } from 'utilities';
import { TokenFactory } from '../../interfaces/bimplus-websdk/interfaces';
import { AuthStoreService } from '../../../../../data-access/auth/auth-store.service';
import { AuthenticationAdapter } from '../../endpoint-adapters';
import { EncryptedStorageService } from '../../../../services/';
import {
  EventTypes,
  LoginResponse,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { filter, switchMap, tap } from 'rxjs';
import { EventBusService } from '../../../../services/event-bus/event-bus.service';
import { UserMapper } from '@web-portal/domains/user/anti-corruption-layer';
import { UserEventBus } from '@web-portal/domains/shared/event-bus';

/**
 * Default implementation of the TokenFactory interface for the Bimplus API.
 * This factory is responsible for setting the access token on the Bimplus API instance using the AuthService to retrieve the token.
 */
export class BimplusTokenFactory implements TokenFactory {
  private readonly authStoreService = inject(AuthStoreService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly encryptedStorageService = inject(EncryptedStorageService);
  private readonly destroyRef$ = inject(DestroyRef);
  private readonly eventBusService = inject(EventBusService);
  private readonly userEventBus = inject(UserEventBus);
  private readonly eventService = inject(PublicEventsService);
  private readonly injector = inject(Injector);
  api: Api | null = null;

  constructor() {
    this.eventService
      .registerForEvents()
      .pipe(
        takeUntilDestroyed(),
        // Wait till we receive silent-renew event and then get & set new access token.
        filter(notification => notification.type === EventTypes.NewAuthenticationResult),
        switchMap(() => this.oidcSecurityService.getAccessToken())
      )
      .subscribe(value => {
        this.setAccessTokenAndType(value);
      });
  }

  setApi(api: Api): void {
    this.api = api;
  }

  setToken(accessToken?: string) {
    if (accessToken) {
      this.initializeStorageForUserWithToken(accessToken);
      return;
    }

    this.authStoreService.accessToken$.pipe(takeUntilDestroyed(this.destroyRef$)).subscribe({
      next: token => this.setAccessTokenAndType(token),
    });
  }

  validateToken(token: string) {
    return this.injector
      .get(AuthenticationAdapter)
      .getTokenInfo(token)
      .pipe(
        tap(token =>
          this.eventBusService.emit({
            type: 'tokenValidated',
            payload: token,
          })
        )
      );
  }

  private async initializeStorageForUserWithToken(accessToken: string) {
    this.setAccessTokenAndType(accessToken);
    const parsedToken = parseToken(accessToken);
    this.userEventBus.emit({
      type: '[User] initialized',
      payload: UserMapper.mapToUser(parsedToken),
    });
    try {
      await this.encryptedStorageService.initializeStorageForUser({
        isAuthenticated: true,
        userData: {
          email: parsedToken?.['email'],
          sub: parsedToken?.['sub'],
        },
        accessToken,
        idToken: '',
      } satisfies LoginResponse);
    } catch (error) {
      console.error('Failed to initialize encrypted storage for user:', error);
    }
  }

  private setAccessTokenAndType(token: string): void {
    if (!this.api) {
      console.error('Bimplus API instance is not set. Cannot set access token.');
      return;
    }

    if (!token.length) {
      return;
    }

    this.api.setAccessTokenAndType(token, 'Bearer');
  }
}
